import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Flex,
  Space,
  Table,
  Typography,
  Form,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import {
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import formatPhone from "helpers/phone_formatter";
import { useAuth } from "context/providers/auth";
import { getIssueStatusLabel } from "helpers/options/issue_statuses";
import useLocalStorageCache from "hooks/useLocalStorageCache";
import useUpdate from "hooks/useUpdate";

import EllipsisText from "components/EllipsisText";
import DisconnectIssueModal from "./components/DisconnectIssueModal";
import GenerateSubIssuesModal from "./components/GenerateSubIssuesModal";
import RevertIssueGroup from "./components/RevertIssueGroup";
import EditableCell from "./components/EditableCell";
import IssueCalendar from "components/IssueCalendar";
import CreateNextSubIssue from "./components/CreateNextSubIssue";
import DeleteSelected from "./components/DeleteSelected";

const SubIssues = ({
  issue_group_id,
  issue_id,
  sub_issues,
  refetch,
  client_phone,
}) => {
  const table_ref = useRef();

  const {
    state: {
      user: { company },
    },
  } = useAuth();

  const [selectedRowKeys, setSelectedRowKeys] = useLocalStorageCache(
    `${company.id}_issue_${issue_id}_sub_issues`,
    []
  );
  const onSelectChange = (newSelectedRowKeys) =>
    setSelectedRowKeys(newSelectedRowKeys);

  const [form] = Form.useForm();
  const [is_open_generate_sub_issues_modal, setIsOpenGenerateSubIssuesModal] =
    useState(false);
  const [editing_id, setEditingId] = useState(null);
  const [is_dirty, setDirty] = useState(false);
  const isEditing = (record) => record.id === editing_id;

  const edit = (record) => {
    form.setFieldsValue(record);
    setEditingId(record.id);
  };

  const cancel = () => {
    setEditingId(null);
    setDirty(false);
  };

  const { is_saving, updateAsync } = useUpdate({
    path: `/issues/${editing_id}`,
    onSuccess: () => {},
  });

  const save = async () => {
    try {
      const values = await form.validateFields();
      await updateAsync(values);
      await refetch();
      setEditingId(null);
      setDirty(false);
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };
  const navigate = useNavigate();

  const data = useMemo(
    () =>
      sub_issues.map((sub_issue, index) => ({
        lp: index + 1,
        key: sub_issue?.id,
        refetch,
        navigate,
        ...sub_issue,
        worker_id: sub_issue?.worker?.id,
        service_date: sub_issue?.service_date
          ? dayjs(sub_issue.service_date)
          : null,
      })),
    [sub_issues, refetch, navigate]
  );

  const columns = [
    {
      title: "Lp.",
      dataIndex: "lp",
      width: 40,
    },
    {
      title: "Ulica",
      dataIndex: "client_street",
      editable: true,
      // width: 250,
      render: (_, { id, client_street }) => (
        <Tooltip title="Przejdź do edycji">
          <Link to={`/issues/${id}`}>
            {client_street} <EditOutlined />
          </Link>
        </Tooltip>
      ),
    },
    {
      title: "Numer budynku",
      dataIndex: "client_building_number",
      width: 100,
      editable: true,
    },
    {
      title: "Numer mieszkania",
      dataIndex: "client_apartment_number",
      width: 100,
      editable: true,
    },
    {
      title: "Telefon",
      dataIndex: "client_phone",
      render: (client_phone) => formatPhone(client_phone),
      width: 130,
      editable: true,
    },
    {
      title: "Data umówienia",
      dataIndex: "service_date",
      width: 190,
      editable: true,
      render: (service_date, data) => (
        <Flex justify="space-between" align="center" gap={12}>
          <p>
            {service_date
              ? dayjs(service_date).format("DD-MM-YYYY HH:mm")
              : "-"}
          </p>
          <IssueCalendar
            origin_source="ze zbiorczego zgłoszenia"
            issue_id={data.id}
            handleConfirm={data.refetch}
            disabled={false}
            show_calendar_icon={true}
            tooltip="Kalendarz"
          />
        </Flex>
      ),
    },
    {
      title: "Pracownik",
      dataIndex: "worker_id",
      editable: true,
      width: 250,
      render: (_, { worker }) =>
        worker?.id ? (
          <Space className="text-nowrap">
            <Avatar style={{ backgroundColor: worker?.color }}>
              {worker.initials}
            </Avatar>
            <Typography.Text delete={worker?.is_deleted}>
              <EllipsisText text={worker.full_name} limit={30} />
            </Typography.Text>
          </Space>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      editable: true,
      render: (status) => getIssueStatusLabel(status),
      width: 120,
    },
    {
      title: "Ilość komentarzy",
      dataIndex: "issue_comments_count",
      editable: false,
      width: 120,
    },
    {
      title: "",
      key: "operation",
      fixed: "end",
      width: 120,
      render: (_, { refetch, navigate, issue_group_item, ...record }) => {
        const editable = isEditing(record);
        return (
          <Flex gap={10}>
            {editable ? (
              <>
                <Tooltip title="Zapisz zmiany">
                  <Button
                    ghost
                    onClick={save}
                    loading={is_saving}
                    disabled={!is_dirty}
                    type="primary"
                    htmlType="button"
                    icon={<CheckOutlined />}
                  />
                </Tooltip>
                <Tooltip title="Anuluj edycję">
                  <Button
                    onClick={cancel}
                    disabled={is_saving}
                    ghost
                    danger
                    type="primary"
                    htmlType="button"
                    icon={<CloseOutlined />}
                  />
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="Edycja wiersza">
                  <Button
                    ghost
                    disabled={editing_id !== null}
                    onClick={() => edit(record)}
                    type="primary"
                    htmlType="button"
                    icon={<FormOutlined />}
                  />
                </Tooltip>
                <DisconnectIssueModal
                  identifier={record.identifier}
                  is_disabled={editing_id !== null}
                  issue_id={record.id}
                  refetch={refetch}
                />
              </>
            )}
          </Flex>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        is_saving: is_saving,
      }),
    };
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: null,
    columnWidth: 48,
  };

  const totals = useMemo(() => {
    if (sub_issues?.length > 0) {
      const completed = sub_issues.filter(({ status }) =>
        ["completed"].includes(status)
      )?.length;

      return {
        completed,
        total: sub_issues.length,
      };
    }
    return {
      completed: 0,
      total: 0,
    };
  }, [sub_issues]);

  useEffect(() => {
    const selected_issues = sub_issues.filter(({ id }) =>
      selectedRowKeys.includes(id)
    );
    if (selected_issues.length > 0) {
      const index = sub_issues.findIndex(
        ({ id }) => selected_issues[0]?.id === id
      );
      if (index > 0) {
        table_ref?.current?.scrollTo({ index });
      }
    }
    // eslint-disable-next-line
  }, [table_ref]);

  return (
    <div
      className="m-b-20"
      style={{ marginBottom: "24px", borderRadius: 8, overflow: "hidden" }}
    >
      {is_open_generate_sub_issues_modal && (
        <GenerateSubIssuesModal
          sub_issues={sub_issues}
          current_issue_id={issue_id}
          issue_group_id={issue_group_id}
          refetch={refetch}
          handleClose={() => setIsOpenGenerateSubIssuesModal(false)}
        />
      )}
      <Form
        form={form}
        component={false}
        onFieldsChange={() => {
          if (is_dirty) return;

          setDirty(true);
        }}
      >
        <Table
          virtual
          ref={table_ref}
          rowSelection={rowSelection}
          columns={mergedColumns}
          components={{
            body: { cell: EditableCell },
          }}
          dataSource={data}
          pagination={false}
          scroll={{ x: "max-content", y: 200 }}
          size="small"
          rowClassName="editable-row"
          footer={() => (
            <Flex justify="space-between" align="center" gap={10} wrap="wrap">
              <span>
                Ukończono: {totals?.completed}/{totals?.total}
              </span>
              <Flex justify="end" gap={8}>
                <CreateNextSubIssue
                  issue_group_id={issue_group_id}
                  refetch={refetch}
                  sub_issues={sub_issues}
                  client_phone={client_phone}
                />
                <Button
                  type="primary"
                  onClick={() => setIsOpenGenerateSubIssuesModal(true)}
                >
                  Dodaj podzgłoszenia
                </Button>
                <RevertIssueGroup
                  is_disabled={sub_issues?.length > 0}
                  issue_group_id={issue_group_id}
                  refetch={refetch}
                />
                {selectedRowKeys?.length > 0 && (
                  <DeleteSelected
                    issue_group_id={issue_group_id}
                    ids={selectedRowKeys}
                    refetch={refetch}
                    clearSelected={() => setSelectedRowKeys([])}
                  />
                )}
              </Flex>
            </Flex>
          )}
        />
      </Form>
    </div>
  );
};

export default SubIssues;
